import { useContext, useEffect, useState } from 'react';
import { Box, Flex } from 'components/box';
import { Text } from 'components/typography';
import Button from 'components/button';
import { ProductInterface } from 'types/types';
import {
  Facebook as FacebookIcon,
  Share as IosShareIcon,
  Share2 as ShareIcon,
} from 'react-feather';
import MailIcon from './mail.svg';
import WhatsappIcon from './whatsapp.svg';
import dynamic from 'next/dynamic';
import { ClientOnly } from 'components/_shared/widgets/render-control';
import useMedia from 'hooks/use-media';
import { css, Theme, ThemeContext } from '@emotion/react';
import { isBrowserWindowAvailable } from '../../../../util/is-browser-window-available';
import Tooltip from 'components/tooltip';
import { Link } from 'components/configurable-routing';

const SocialSharingModal = dynamic(
  () => import('components/_pages/products/modals/social-sharing')
);

const getMobileDetect = (userAgent: NavigatorID['userAgent']) => ({
  isIos: Boolean(userAgent.match(/iPhone|iPad|iPod/i)),
});

const useMobileDetect = () => {
  const [device, setDevice] = useState({ isIos: false });

  useEffect(() => {
    if (typeof navigator !== 'undefined') {
      setDevice(getMobileDetect(navigator.userAgent));
    }
  }, []);

  return device;
};

const socialIconStyles = (theme: Theme) =>
  css`
    background-color: ${theme.colors.lightGrey};
    padding: 2px;
    border-radius: 100%;
    width: 34px;
    height: 34px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: all 0.2s ease;

    span {
      color: ${theme.colors.darkGrey};
    }

    &:hover {
      background-color: ${theme.colors.mediumGrey};
    }

    @media ${theme.mediaQueries.mobileOnly} {
      width: 50px;
      height: 50px;
    }

    & :first-child {
      margin-left: 0 !important;
    }
  `;

const SocialShareButton = ({ onClick }: { onClick: () => void }) => {
  const currentDevice = useMobileDetect();
  return (
    <Button
      variant="flat"
      onClick={onClick}
      mb={0}
      p={0}
      css={theme => css`
        text-transform: none;

        &:hover {
          color: ${theme.colors.primary};
          svg {
            stroke: ${theme.colors.primary};
          }
        }
      `}
    >
      <Flex
        justifyContent={['flex-end', null, 'flex-start']}
        alignItems="center"
      >
        {currentDevice.isIos && <IosShareIcon size={20} color="black" />}
        {!currentDevice.isIos && <ShareIcon size={20} color="black" />}
      </Flex>
    </Button>
  );
};

const SharingActions = ({
  product,
  isMobile,
}: {
  product: ProductInterface;
  isMobile: boolean;
}) => {
  const [baseDomain, setBaseDomain] = useState('');
  const [isSocialModalOpen, setIsSocialModalOpen] = useState(false);

  const url = encodeURIComponent(`${baseDomain}`);

  // explicitly encode all URI components before the URL is built
  const encodedProductName = encodeURIComponent(product.name);

  const whatsappMessage =
    `Hey!` +
    `%0A` +
    `%0AI know how much you love cool stuff! I saw this on OneDayOnly and I had to share it with you. ` +
    `It%27s kind of a big deal:` +
    `%0A` +
    `%0A${encodedProductName}` +
    `%0A` +
    `%0AGo on, have a look:` +
    `%0A${url}`;

  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${baseDomain}`;
  const mailUrl =
    `mailTo:?subject=Here%27s the deal! Check out this amazing product on OneDayOnly&body=Hey!` +
    `%0A` +
    `%0AI know you%27ve received too many emails today, but I also know how much you love cool stuff!` +
    `%0A` +
    `%0AI saw this on OneDayOnly and I had to share it with you.` +
    `%0A` +
    `%0AIt%27s kind of a big deal:` +
    `%0A` +
    `%0A${encodedProductName}` +
    `%0A` +
    `%0A` +
    `%0AGo on, have a look:` +
    `%0A${url}`;

  const whatsappUrl = isMobile
    ? `whatsapp://send?text=${whatsappMessage}`
    : `https://wa.me?text=${whatsappMessage}`;

  useEffect(() => {
    isBrowserWindowAvailable() && setBaseDomain(window.location.href);
  }, []);

  return (
    <>
      {isMobile ? (
        <Flex>
          <div css={socialIconStyles}>
            <SocialShareButton onClick={() => setIsSocialModalOpen(true)} />
          </div>
        </Flex>
      ) : (
        <Flex
          mt={2}
          flexDirection="row"
          css={css`
            gap: 1em;
          `}
        >
          <div css={socialIconStyles}>
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
              <Tooltip content="Share on Whatsapp" placement="top">
                <WhatsappIcon size={18} />
              </Tooltip>
            </a>
          </div>
          <div css={socialIconStyles}>
            <a href={mailUrl} target="_blank" rel="noopener noreferrer">
              <Tooltip content="Share via email" placement="top">
                <MailIcon size={10} />
              </Tooltip>
            </a>
          </div>
          <div css={socialIconStyles}>
            <a href={facebookUrl}>
              <Tooltip content="Share on Facebook" placement="top">
                <FacebookIcon stroke="none" fill="#747e86" size={20} />
              </Tooltip>
            </a>
          </div>
        </Flex>
      )}

      <SocialSharingModal
        close={() => setIsSocialModalOpen(false)}
        isOpen={isSocialModalOpen}
      >
        <Link
          href={whatsappUrl}
          title="Whatsapp"
          target="_blank"
          prefetch={false}
          rel="noopener noreferrer"
        >
          <Flex mb={3} alignItems="center">
            <Box css={socialIconStyles}>
              <WhatsappIcon size={30} />
            </Box>
            <Text fontSize={2} fontWeight="bold" color="darkGrey">
              Whatsapp
            </Text>
          </Flex>
        </Link>

        <Link
          href={mailUrl}
          title="Mail"
          target="_blank"
          prefetch={false}
          rel="noopener noreferrer"
        >
          <Flex mb={3} alignItems="center">
            <Box css={socialIconStyles}>
              <MailIcon size={30} />
            </Box>
            <Text fontSize={2} fontWeight="bold" color="darkGrey">
              Email
            </Text>
          </Flex>
        </Link>

        <Link
          href={facebookUrl}
          title="Facebook"
          target="_blank"
          prefetch={false}
          rel="noopener noreferrer"
        >
          <Flex alignItems="center">
            <Box mr={3} css={socialIconStyles}>
              <FacebookIcon stroke="none" fill="#747e86" size={30} />
            </Box>
            <Text fontSize={2} fontWeight="bold" color="darkGrey">
              Facebook
            </Text>
          </Flex>
        </Link>
      </SocialSharingModal>
    </>
  );
};

const Wrapper = ({ product }: { product: ProductInterface }) => {
  const theme = useContext(ThemeContext) as Theme;
  const isMobile = useMedia(theme.mediaQueries.mobileOnly);

  return (
    <ClientOnly>
      <SharingActions product={product} isMobile={isMobile} />
    </ClientOnly>
  );
};

export default Wrapper;
